import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { QRCodeCanvas } from "qrcode.react";
import { api } from "../../lib/api";
import { downloadPDF, openPDF, THEME_ASSETS } from "../../lib/pdf";
import { COMMON_DRUGS, doseFromMgKgRule } from "../../lib/calc";
import { FileText, FlaskConical, Plus, Trash2, Download, QrCode, Sparkles, Loader2, ShieldCheck, Palette, Info, Printer, Eraser, X, BookmarkPlus, BookMarked } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { AiDisclaimer } from "../AiDisclaimer";

const THEMES = Object.keys(THEME_ASSETS);

// Input con botón ✕ para limpiar ese campo individualmente
const ClearInput = ({ value, onChange, onClear, placeholder, testid, extraRight = null }) => (
  <div className="relative">
    <Input value={value || ""} onChange={onChange} placeholder={placeholder} data-testid={testid}
      className="rounded-lg bg-white border-transparent text-sm pr-14" />
    <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
      {extraRight}
      {value ? (
        <button type="button" onClick={onClear} title="Limpiar este campo" className="text-muted-foreground hover:text-brand-terracotta">
          <X size={14} />
        </button>
      ) : null}
    </div>
  </div>
);

export default function PrescriptionPanel({ patient, pediatric = true, prescriptions, onChange, prefill, latestWeight, ageMonths, only = ["receta", "estudio", "consentimiento"] }) {
  const [kind, setKind] = useState(only[0]);
  const [theme, setTheme] = useState("clasica");
  const [rxFormat, setRxFormat] = useState(() => localStorage.getItem("clinped_rx_format") || "completa");
  const [diagnosis, setDiagnosis] = useState("");
  const [indications, setIndications] = useState("");
  const [items, setItems] = useState([{}]);
  const [saving, setSaving] = useState(false);
  const [shareRx, setShareRx] = useState(null);
  const [drugLoading, setDrugLoading] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [savedDrugs, setSavedDrugs] = useState([]);
  const [pickerFor, setPickerFor] = useState(null); // item index for saved-drugs picker
  const [savingDrug, setSavingDrug] = useState(null);
  const [showAllDocs, setShowAllDocs] = useState(false);
  // consentimiento
  const [procedure, setProcedure] = useState("");
  const [consentText, setConsentText] = useState("");
  const [risks, setRisks] = useState("");
  const [benefits, setBenefits] = useState("");
  const [alternatives, setAlternatives] = useState("");
  const [guardian, setGuardian] = useState(patient.guardian_name || "");
  const [consentLoading, setConsentLoading] = useState(false);
  const [consentTemplates, setConsentTemplates] = useState([]);
  const [tplDialog, setTplDialog] = useState(false);
  const [tplName, setTplName] = useState("");

  useEffect(() => {
    if (prefill) {
      setKind("receta");
      setItems(prefill.items?.length ? prefill.items : [{}]);
      setIndications(prefill.indications || "");
      setDiagnosis(prefill.diagnosis || "");
    }
  }, [prefill]);

  const loadSavedDrugs = async () => {
    try { const res = await api.get("/saved-drugs"); setSavedDrugs(res.data || []); }
    catch (e) { /* silencioso */ }
  };
  const [dbDrugs, setDbDrugs] = useState([]);
  useEffect(() => { loadSavedDrugs(); }, []);
  useEffect(() => { if (pediatric) api.get("/drugs").then((r) => setDbDrugs(r.data || [])).catch(() => {}); }, [pediatric]);
  const loadTemplates = () => api.get("/consent-templates").then((r) => setConsentTemplates(r.data || [])).catch(() => {});
  useEffect(() => { loadTemplates(); }, []);

  const applyTemplate = (t) => {
    setProcedure(t.procedure || "");
    setConsentText(t.consent_text || "");
    setBenefits((t.benefits || []).join("\n"));
    setRisks((t.risks || []).join("\n"));
    setAlternatives((t.alternatives || []).join("\n"));
    setTplDialog(false);
    toast.success(`Plantilla "${t.name}" aplicada`);
  };

  const saveTemplate = async () => {
    const name = (tplName || procedure).trim();
    if (!name) { toast.error("Escribe un nombre o procedimiento"); return; }
    try {
      await api.post("/consent-templates", {
        name, procedure,
        consent_text: consentText,
        benefits: benefits.split("\n").filter(Boolean),
        risks: risks.split("\n").filter(Boolean),
        alternatives: alternatives.split("\n").filter(Boolean),
      });
      toast.success("Plantilla guardada");
      setTplName("");
      loadTemplates();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`¿Eliminar la plantilla "${t.name}"?`)) return;
    try { await api.delete(`/consent-templates/${t.template_id}`); setConsentTemplates((s) => s.filter((x) => x.template_id !== t.template_id)); }
    catch { toast.error("Error"); }
  };

  const saveDrug = async (i) => {
    const it = items[i];
    if (!it?.name) { toast.error("Escribe el fármaco antes de guardar"); return; }
    setSavingDrug(i);
    try {
      await api.post("/saved-drugs", {
        name: it.name, presentation: it.presentation || "", presentaciones: it.presentaciones || null,
        marca_ejemplos: it.marca_ejemplos || "", dose: it.dose || "", dose_detail: it.dose_detail || "",
        dose_mg_kg: it.dose_mg_kg || "",
        route: it.route || "", frequency: it.frequency || "", duration: it.duration || "",
      });
      toast.success("Fármaco guardado en tu banco (se recalculará por peso)");
      loadSavedDrugs();
    } catch (e) { toast.error("No se pudo guardar"); }
    finally { setSavingDrug(null); }
  };

  const applySaved = (i, d) => {
    // Recalcula la dosis por el peso del paciente actual usando la regla mg/kg guardada.
    let dose = d.dose;
    let recalculated = false;
    if (pediatric && d.dose_mg_kg && latestWeight) {
      const r = doseFromMgKgRule(d.dose_mg_kg, latestWeight, d.presentation);
      if (r) { dose = r.text; recalculated = true; }
    }
    setItems((arr) => arr.map((it, idx) => idx === i ? {
      ...it, name: d.name, presentation: d.presentation, presentaciones: d.presentaciones,
      marca_ejemplos: d.marca_ejemplos, dose, dose_detail: d.dose_detail, dose_mg_kg: d.dose_mg_kg,
      route: d.route, frequency: d.frequency, duration: d.duration, warning: undefined,
    } : it));
    setPickerFor(null);
    if (recalculated) toast.success(`Dosis recalculada para ${latestWeight} kg (regla ${d.dose_mg_kg}). Sin IA.`);
    else toast.success("Cargado desde tu banco (sin IA). Verifica la dosis según el peso.");
  };

  const deleteSaved = async (id) => {
    try { await api.delete(`/saved-drugs/${id}`); setSavedDrugs((s) => s.filter((d) => d.saved_drug_id !== id)); }
    catch (e) { toast.error("No se pudo eliminar"); }
  };

  const deletePrescription = async (rx) => {
    if (!window.confirm("¿Eliminar este documento? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/prescriptions/${rx.prescription_id}`);
      toast.success("Documento eliminado");
      onChange?.();
    } catch (e) { toast.error("No se pudo eliminar"); }
  };

  const shareUrl = (rx) => `${window.location.origin}/receta/${rx.share_id}`;
  const auditDoc = (action, rx) =>
    api.post("/audit/log", { action, entity: rx.kind || "documento", entity_id: rx.prescription_id, patient_id: patient.patient_id }).catch(() => {});
  const addItem = () => setItems([...items, {}]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updItem = (i, k, v) => setItems(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const clearItem = (i) => setItems(items.map((it, idx) => (idx === i ? {} : it)));

  const quickDrug = (i, d) => {
    const w = parseFloat(latestWeight);
    let dose = "";
    if (w) {
      if (d.byDose) dose = `${+(w * d.mgPerKgDose).toFixed(1)} mg/dosis`;
      else dose = `${+(w * d.mgPerKgDay / d.doses).toFixed(1)} mg/dosis (${+(w * d.mgPerKgDay).toFixed(0)} mg/día)`;
    }
    updItem(i, "name", d.name);
    updItem(i, "dose", dose);
    updItem(i, "frequency", d.note);
  };

  const mapLocalDrug = (it, res) => {
    const vol = res.volumenes && res.volumenes[0];
    let dose, presentation;
    if (res.por_peso === false) {
      dose = res.dosis_texto || it.dose;
      presentation = res.presentaciones?.[0]?.label || it.presentation;
    } else if (vol) {
      dose = `${vol.ml_per_dose} mL/dosis`;
      presentation = vol.label;
    } else {
      dose = `${res.per_dose_mg} mg/dosis`;
      presentation = res.presentaciones?.[0]?.label || it.presentation;
    }
    const detail = [];
    if (res.por_peso) {
      detail.push(`${res.per_dose_mg} mg/dosis`, `${res.per_day_mg} mg/día`, `${res.doses_per_day} tomas/día`, `mg/kg: ${res.mg_kg}`);
      if (res.capped) detail.push("(tope de dosis alcanzado)");
    } else if (res.dosis_texto) detail.push(res.dosis_texto);
    detail.push(`Vía: ${res.via}`);
    return {
      ...it,
      name: res.generico || it.name,
      presentation,
      presentaciones: (res.presentaciones || []).map((p) => p.label),
      dose,
      dose_detail: detail.join("  ·  "),
      dose_mg_kg: res.por_peso ? `${res.mg_kg} mg/kg/${res.calc === "per_dose" ? "dosis" : "día"}` : (it.dose_mg_kg || ""),
      route: res.via || it.route,
      frequency: res.interval_text || it.frequency,
      marca_ejemplos: res.marcas || it.marca_ejemplos,
      warning: "",
    };
  };

  const tryLocalDrug = async (i) => {
    const name = (items[i]?.name || "").trim();
    if (!(pediatric && latestWeight && name.length >= 3)) return false;
    try {
      const list = (await api.get("/drugs", { params: { q: name } })).data || [];
      if (!list.length) return false;
      const res = (await api.get("/drugs/calculate", { params: { drug_id: list[0].id, weight: parseFloat(latestWeight), age_months: ageMonths } })).data;
      if (!res) return false;
      setItems((arr) => arr.map((it, idx) => (idx === i ? mapLocalDrug(it, res) : it)));
      return true;
    } catch (e) { return false; }
  };

  const completeDrug = async (i) => {
    const name = items[i]?.name;
    if (!name) { toast.error("Escribe el fármaco (principio activo o marca)"); return; }
    setDrugLoading(i);
    try {
      // 1) Cálculo LOCAL por peso (sin IA) si el fármaco está en la base pediátrica.
      if (await tryLocalDrug(i)) {
        toast.success(`Dosis calculada por peso (${latestWeight} kg) sin IA`);
        setDrugLoading(null);
        return;
      }
      // 2) Respaldo con IA (fármaco no listado o paciente adulto/sin peso).
      const res = await api.post("/ai/drug", { drug: name, weight: (pediatric && latestWeight) ? parseFloat(latestWeight) : null, age_months: ageMonths });
      const p = res.data.parsed;
      if (!p) { toast.error("La IA no devolvió datos"); return; }
      setItems((arr) => arr.map((it, idx) => idx === i ? {
        ...it,
        name: p.principio_activo || it.name,
        presentation: p.presentacion || it.presentation,
        presentaciones: Array.isArray(p.presentaciones) && p.presentaciones.length ? p.presentaciones : it.presentaciones,
        dose: p.dosis_receta || p.dosis_calculada || it.dose,
        dose_detail: [p.dosis_calculada, p.dosis_mg_kg && `mg/kg: ${p.dosis_mg_kg}`, p.dosis_maxima && `Máx: ${p.dosis_maxima}`, p.via && `Vía: ${p.via}`].filter(Boolean).join("  ·  "),
        dose_mg_kg: p.dosis_mg_kg || it.dose_mg_kg,
        route: p.via || it.route,
        frequency: p.frecuencia || it.frequency,
        duration: p.duracion || it.duration,
        warning: p.advertencias,
        marca_ejemplos: p.marca_ejemplos || it.marca_ejemplos,
      } : it));
      if (p.advertencias) toast.warning(p.advertencias, { duration: 6000 });
      else toast.success("Información completada con IA");
    } catch (e) { toast.error("Error de IA"); }
    finally { setDrugLoading(null); }
  };

  const generateConsent = async () => {
    if (!procedure) { toast.error("Escribe el procedimiento"); return; }
    setConsentLoading(true);
    try {
      const res = await api.post("/ai/assist", { patient_id: patient.patient_id, type: "consent", procedure });
      const p = res.data.parsed;
      if (!p) { toast.error("La IA no devolvió datos"); return; }
      setConsentText(p.texto_consentimiento || p.descripcion || "");
      setBenefits((p.beneficios || []).join("\n"));
      setRisks((p.riesgos || []).join("\n"));
      setAlternatives((p.alternativas || []).join("\n"));
      toast.success("Consentimiento generado (revísalo y edítalo)");
    } catch (e) { toast.error("Error de IA"); }
    finally { setConsentLoading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      let payload;
      if (kind === "consentimiento") {
        if (!procedure) { toast.error("Escribe el procedimiento"); setSaving(false); return; }
        payload = {
          patient_id: patient.patient_id, kind, theme, procedure,
          consent_text: consentText, guardian_name: guardian,
          risks: risks.split("\n").filter(Boolean),
          benefits: benefits.split("\n").filter(Boolean),
          alternatives: alternatives.split("\n").filter(Boolean),
        };
      } else {
        const cleaned = items.filter((it) => (kind === "receta" ? it.name : it.study));
        if (cleaned.length === 0 && !(kind === "receta" && indications.trim())) { toast.error(kind === "receta" ? "Agrega un fármaco o al menos indicaciones" : "Agrega al menos un estudio"); setSaving(false); return; }
        payload = { patient_id: patient.patient_id, kind, theme, items: cleaned, indications: kind === "receta" ? indications : "", diagnosis };
      }
      if (kind === "receta") payload.format = rxFormat;
      const res = await api.post("/prescriptions", payload);
      toast.success("Documento generado");
      if (kind !== "consentimiento") { setItems([{}]); setIndications(""); setDiagnosis(""); }
      else { setProcedure(""); setConsentText(""); setRisks(""); setBenefits(""); setAlternatives(""); }
      onChange?.();
      if (kind === "receta") loadSavedDrugs();
      setShareRx(res.data);
    } catch (e) { toast.error("Error al generar"); }
    finally { setSaving(false); }
  };

  const KIND_BTN = [
    { k: "receta", l: "Receta", icon: FileText, active: "bg-brand-terracotta text-white" },
    { k: "estudio", l: "Estudios", icon: FlaskConical, active: "bg-brand-sage text-white" },
    { k: "consentimiento", l: "Consentimiento", icon: ShieldCheck, active: "bg-brand-ink text-white" },
  ].filter((b) => only.includes(b.k));
  const isConsentOnly = only.length === 1 && only[0] === "consentimiento";
  const docList = prescriptions.filter((rx) => isConsentOnly ? rx.kind === "consentimiento" : rx.kind !== "consentimiento");

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(61,64,91,0.04)] border border-stone-100 space-y-4">
        {KIND_BTN.length > 1 && (
        <div className="flex bg-muted rounded-full p-1 w-fit flex-wrap">
          {KIND_BTN.map((b) => (
            <button key={b.k} data-testid={`kind-${b.k}`} onClick={() => setKind(b.k)}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-bold transition-all ${kind === b.k ? b.active : "text-brand-ink/60"}`}>
              <b.icon size={15} /> {b.l}
            </button>
          ))}
        </div>
        )}

        {/* Theme selector */}
        {kind !== "estudio" && (
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/50 flex items-center gap-1 mb-1.5"><Palette size={13} /> Plantilla (decorativa, no estorba la escritura)</p>
            <div className="flex flex-wrap gap-2">
              {THEMES.map((t) => (
                <button key={t} data-testid={`theme-${t}`} onClick={() => setTheme(t)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${theme === t ? "bg-brand-terracotta text-white" : "bg-muted text-brand-ink/60"}`}>
                  {THEME_ASSETS[t].label}
                </button>
              ))}
            </div>
          </div>
        )}

        {kind === "receta" && (
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/50 flex items-center gap-1 mb-1.5"><FileText size={13} /> Formato de impresión</p>
            <div className="grid grid-cols-2 gap-2">
              {[["completa", "Completa (carta)", "Formato NOM-004 vertical"],
                ["media_carta", "Media carta", "Compacta, con somatometría al lado"]].map(([val, title, desc]) => (
                <button key={val} type="button" data-testid={`rx-format-${val}`}
                  onClick={() => { setRxFormat(val); localStorage.setItem("clinped_rx_format", val); }}
                  className={`text-left rounded-xl border-2 px-3 py-2 transition-all ${rxFormat === val ? "border-brand-terracotta bg-brand-terracotta/5" : "border-stone-200 hover:border-stone-300"}`}>
                  <p className="font-semibold text-xs text-brand-ink">{title}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {kind === "consentimiento" ? (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input data-testid="consent-procedure" value={procedure} onChange={(e) => setProcedure(e.target.value)} placeholder="Procedimiento (ej. sutura, nebulización)" className="rounded-xl bg-stone-50 border-transparent focus:bg-white" />
              <button data-testid="consent-ai-btn" onClick={generateConsent} disabled={consentLoading}
                className="shrink-0 flex items-center gap-1.5 bg-brand-ink text-white px-3 rounded-xl text-sm font-bold disabled:opacity-60">
                {consentLoading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} IA
              </button>
            </div>
            <div className="flex gap-2">
              <button data-testid="consent-templates-btn" onClick={() => { loadTemplates(); setTplDialog(true); }}
                className="flex-1 flex items-center justify-center gap-1.5 text-xs font-bold text-brand-ink border border-stone-200 rounded-xl py-2 hover:bg-stone-50">
                <BookMarked size={14} /> Plantillas guardadas {consentTemplates.length ? `(${consentTemplates.length})` : ""}
              </button>
              <button data-testid="consent-save-template-btn" onClick={saveTemplate}
                className="flex-1 flex items-center justify-center gap-1.5 text-xs font-bold text-brand-sageDark border border-brand-sage/40 rounded-xl py-2 hover:bg-brand-sage/10">
                <BookmarkPlus size={14} /> Guardar como plantilla
              </button>
            </div>
            <Input value={guardian} onChange={(e) => setGuardian(e.target.value)} placeholder="Nombre del padre/madre/tutor" className="rounded-xl bg-stone-50 border-transparent focus:bg-white" />
            <Textarea data-testid="consent-text" value={consentText} onChange={(e) => setConsentText(e.target.value)} placeholder="Texto del consentimiento (editable)" rows={4} className="rounded-xl bg-stone-50 border-transparent focus:bg-white" />
            <Textarea value={benefits} onChange={(e) => setBenefits(e.target.value)} placeholder="Beneficios (uno por línea)" rows={2} className="rounded-xl bg-stone-50 border-transparent focus:bg-white" />
            <Textarea value={risks} onChange={(e) => setRisks(e.target.value)} placeholder="Riesgos (uno por línea)" rows={2} className="rounded-xl bg-stone-50 border-transparent focus:bg-white" />
            <Textarea value={alternatives} onChange={(e) => setAlternatives(e.target.value)} placeholder="Alternativas (una por línea)" rows={2} className="rounded-xl bg-stone-50 border-transparent focus:bg-white" />
          </div>
        ) : (
          <>
            <Input value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} placeholder="Diagnóstico" className="rounded-xl bg-stone-50 border-transparent focus:bg-white" data-testid="rx-diagnosis" />
            <div className="space-y-3">
              {items.map((it, i) => (
                <div key={i} className="bg-stone-50 rounded-2xl p-3 space-y-2 relative">
                  {items.length > 1 && <button onClick={() => removeItem(i)} className="absolute top-2 right-2 text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button>}
                  {kind === "receta" ? (
                    <>
                      <div className="flex gap-2">
                        <Input list={`drugbank-list-${i}`} data-testid={`rx-name-${i}`} value={it.name || ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            const m = savedDrugs.find((d) => (d.name || "").toLowerCase() === v.trim().toLowerCase());
                            if (m) applySaved(i, m); else updItem(i, "name", v);
                          }}
                          onBlur={async () => {
                            if (!items[i]?.dose && (items[i]?.name || "").trim().length >= 4) {
                              if (await tryLocalDrug(i)) toast.success("Dosis calculada por peso (sin IA)");
                            }
                          }}
                          placeholder="Fármaco (principio activo o marca)" className="rounded-lg bg-white border-transparent" />
                        <datalist id={`drugbank-list-${i}`}>
                          {savedDrugs.map((d) => <option key={d.saved_drug_id || d.name} value={d.name}>{[d.presentation, d.dose_mg_kg].filter(Boolean).join(" · ")}</option>)}
                          {dbDrugs.map((d) => <option key={`db-${d.id}`} value={d.generico}>{d.categoria}</option>)}
                        </datalist>
                        <button data-testid={`rx-ai-${i}`} onClick={() => completeDrug(i)} disabled={drugLoading === i}
                          className="shrink-0 flex items-center gap-1 bg-brand-terracotta text-white px-2.5 rounded-lg text-xs font-bold disabled:opacity-60" title="Calcular dosis: local por peso (sin IA) si el fármaco está en la base; IA como respaldo">
                          {drugLoading === i ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} IA
                        </button>
                        <button type="button" data-testid={`rx-saved-${i}`} onClick={() => setPickerFor(i)}
                          className="shrink-0 flex items-center gap-1 bg-brand-sage text-white px-2.5 rounded-lg text-xs font-bold" title="Elegir de tu banco de fármacos (sin IA)">
                          <BookMarked size={14} /> Banco
                        </button>
                      </div>
                      {it.marca_ejemplos && (
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-[10px] font-bold uppercase tracking-wide text-brand-ink/40">Marcas:</span>
                          {it.marca_ejemplos.split(/[,;]/).map((m) => m.trim()).filter(Boolean).map((m) => (
                            <button key={m} type="button" data-testid={`rx-brand-${i}`} onClick={() => updItem(i, "name", m)}
                              className="text-[11px] font-semibold bg-brand-ink/10 text-brand-ink px-2 py-0.5 rounded-full hover:bg-brand-ink/20" title="Usar esta marca en la receta">{m}</button>
                          ))}
                        </div>
                      )}
                      {it.presentaciones && it.presentaciones.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-[10px] font-bold uppercase tracking-wide text-brand-ink/40">Presentaciones:</span>
                          {it.presentaciones.map((pr) => (
                            <button key={pr} type="button" data-testid={`rx-pres-${i}`} onClick={() => updItem(i, "presentation", pr)}
                              className="text-[11px] font-semibold bg-brand-sand/30 text-brand-ink px-2 py-0.5 rounded-full hover:bg-brand-sand/50" title="Usar esta presentación">{pr}</button>
                          ))}
                        </div>
                      )}
                      <ClearInput value={it.presentation} onChange={(e) => updItem(i, "presentation", e.target.value)} onClear={() => updItem(i, "presentation", "")} placeholder="Presentación (ej. solución gotas 100mg/ml)" />
                      <div className="grid grid-cols-2 gap-2">
                        <ClearInput value={it.dose} testid={`rx-dose-${i}`} onChange={(e) => updItem(i, "dose", e.target.value)} onClear={() => updItem(i, "dose", "")} placeholder="Dosis (gotas/ml/mg)"
                          extraRight={it.dose_detail ? (
                            <button type="button" data-testid={`rx-dose-detail-${i}`} onClick={() => setDetailItem(it)} className="text-brand-sage hover:text-brand-sageDark" title="Ver detalle del cálculo"><Info size={15} /></button>
                          ) : null} />
                        <ClearInput value={it.route} onChange={(e) => updItem(i, "route", e.target.value)} onClear={() => updItem(i, "route", "")} placeholder="Vía" />
                        <ClearInput value={it.frequency} onChange={(e) => updItem(i, "frequency", e.target.value)} onClear={() => updItem(i, "frequency", "")} placeholder="Frecuencia (ej. cada 8 hrs)" />
                        <ClearInput value={it.duration} onChange={(e) => updItem(i, "duration", e.target.value)} onClear={() => updItem(i, "duration", "")} placeholder="Duración (ej. 5 días)" />
                      </div>
                      <p className="text-[11px] text-muted-foreground">En la receta se imprime: <b>{it.name || "Fármaco"} {it.presentation || ""}</b> — {(/(oral|v\.?o\.?|boca|tomar)/i.test(it.route || "") || !it.route) ? "Tomar" : "Aplicar"} {[it.dose, it.frequency, it.duration && (/^por/i.test(it.duration) ? it.duration : `por ${it.duration}`)].filter(Boolean).join(" ")}</p>
                      {it.warning && <p className="text-xs text-destructive bg-destructive/5 rounded-lg px-2 py-1">⚠ {it.warning}</p>}
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {pediatric && COMMON_DRUGS.map((d) => (
                          <button key={d.name} onClick={() => quickDrug(i, d)} className="text-[11px] font-semibold bg-brand-sage/15 text-brand-sageDark px-2 py-0.5 rounded-full hover:bg-brand-sage/25">{d.name}</button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <Input data-testid={`rx-study-${i}`} value={it.study || ""} onChange={(e) => updItem(i, "study", e.target.value)} placeholder="Estudio (ej. BH, EGO, Rx tórax)" className="rounded-lg bg-white border-transparent" />
                      <Input value={it.indication || ""} onChange={(e) => updItem(i, "indication", e.target.value)} placeholder="Indicación" className="rounded-lg bg-white border-transparent text-sm" />
                    </>
                  )}
                  <div className="flex justify-end pt-0.5">
                    {kind === "receta" && (
                      <button data-testid={`rx-save-drug-${i}`} onClick={() => saveDrug(i)} disabled={savingDrug === i} title="Guarda este fármaco en tu banco para reusarlo sin IA"
                        className="flex items-center gap-1 text-[11px] font-semibold text-brand-sageDark hover:text-brand-sage mr-auto disabled:opacity-60">
                        {savingDrug === i ? <Loader2 size={12} className="animate-spin" /> : <BookmarkPlus size={12} />} Guardar en banco
                      </button>
                    )}
                    <button data-testid={`rx-clear-${i}`} onClick={() => clearItem(i)} title="Borra todos los campos de este medicamento de una vez"
                      className="flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-brand-terracotta">
                      <Eraser size={12} /> Limpiar todo
                    </button>
                  </div>
                </div>
              ))}
              <button onClick={addItem} className="flex items-center gap-1.5 text-sm font-semibold text-brand-terracotta"><Plus size={16} /> Agregar</button>
            </div>
            {kind === "receta" && (
              <Textarea value={indications} onChange={(e) => setIndications(e.target.value)} placeholder="Indicaciones / recomendaciones generales" rows={2} className="rounded-xl bg-stone-50 border-transparent focus:bg-white" />
            )}
          </>
        )}

        <button data-testid="generate-rx" onClick={save} disabled={saving}
          className="w-full bg-brand-ink hover:opacity-90 text-white font-outfit font-semibold py-3 rounded-2xl transition-all hover:scale-[1.01] active:scale-[0.98] disabled:opacity-60">
          {saving ? "Generando…" : `Generar ${kind === "consentimiento" ? "consentimiento" : kind === "receta" ? "receta" : "estudios"}`}
        </button>
      </div>

      <div className="space-y-3">
        <h3 className="font-outfit font-bold text-lg text-brand-ink">{isConsentOnly ? "Consentimientos generados" : "Documentos generados"}</h3>
        {docList.length === 0 ? (
          <div className="bg-white rounded-3xl p-8 text-center border border-stone-100 text-muted-foreground text-sm">Aún no hay documentos.</div>
        ) : (showAllDocs ? docList : docList.slice(0, 3)).map((rx) => {
          const meta = rx.kind === "consentimiento" ? { i: ShieldCheck, c: "bg-brand-ink/10 text-brand-ink", l: "Consentimiento" }
            : rx.kind === "odontograma" ? { i: FileText, c: "bg-brand-sage/15 text-brand-sageDark", l: "Odontograma" }
            : rx.kind === "estudio" ? { i: FlaskConical, c: "bg-brand-sage/15 text-brand-sageDark", l: "Estudios" }
            : { i: FileText, c: "bg-brand-terracotta/10 text-brand-terracotta", l: "Receta" };
          const Icon = meta.i;
          return (
            <motion.div key={rx.prescription_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-4 border border-stone-100 flex items-center gap-3" data-testid={`rx-item-${rx.prescription_id}`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${meta.c}`}><Icon size={18} /></div>
              <div className="min-w-0 flex-1">
                <p className="font-outfit font-semibold text-sm text-brand-ink">{meta.l}{rx.theme && rx.theme !== "clasica" ? ` · ${THEME_ASSETS[rx.theme]?.label}` : ""}</p>
                <p className="text-xs text-muted-foreground">{new Date(rx.created_at).toLocaleDateString("es-MX")}{rx.folio ? ` · Folio ${rx.folio}` : ""}</p>
              </div>
              <button data-testid={`share-rx-${rx.prescription_id}`} onClick={() => setShareRx(rx)} className="p-2 rounded-xl text-brand-sage hover:bg-brand-sage/10" title="Compartir QR"><QrCode size={18} /></button>
              <button data-testid={`pdf-rx-${rx.prescription_id}`} onClick={() => { auditDoc("descargar", rx); downloadPDF(rx, shareUrl(rx)); }} className="p-2 rounded-xl text-brand-terracotta hover:bg-brand-terracotta/10" title="Descargar PDF"><Download size={18} /></button>
              <button data-testid={`print-rx-${rx.prescription_id}`} onClick={() => { auditDoc("imprimir", rx); openPDF(rx, shareUrl(rx)); }} className="p-2 rounded-xl text-brand-ink hover:bg-muted" title="Abrir / Imprimir PDF"><Printer size={18} /></button>
              <button data-testid={`del-rx-${rx.prescription_id}`} onClick={() => deletePrescription(rx)} className="p-2 rounded-xl text-muted-foreground hover:text-destructive hover:bg-destructive/10" title="Eliminar documento"><Trash2 size={18} /></button>
            </motion.div>
          );
        })}
        {docList.length > 3 && (
          <button data-testid="toggle-all-docs" onClick={() => setShowAllDocs((v) => !v)}
            className="w-full text-sm font-semibold text-brand-sageDark hover:text-brand-sage py-2 rounded-xl hover:bg-brand-sage/5 transition-colors">
            {showAllDocs ? "Ver menos" : `Ver todos (${docList.length})`}
          </button>
        )}
      </div>

      <Dialog open={!!shareRx} onOpenChange={() => setShareRx(null)}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader><DialogTitle className="font-outfit text-brand-ink">Compartir con el paciente</DialogTitle></DialogHeader>
          {shareRx && (
            <div className="flex flex-col items-center gap-4 py-2">
              <div className="p-4 bg-white rounded-3xl border-2 border-brand-sage/30 shadow-lg" data-testid="qr-code">
                <QRCodeCanvas value={shareUrl(shareRx)} size={200} fgColor="#3D405B" level="M" />
              </div>
              <p className="text-sm text-muted-foreground text-center">El paciente escanea el código para ver y descargar su documento.</p>
              <div className="flex gap-2 w-full">
                <button onClick={() => { navigator.clipboard.writeText(shareUrl(shareRx)); toast.success("Enlace copiado"); }} className="flex-1 bg-muted text-brand-ink font-semibold py-2.5 rounded-xl text-sm">Copiar enlace</button>
                <button onClick={() => downloadPDF(shareRx, shareUrl(shareRx))} className="flex-1 bg-brand-terracotta text-white font-semibold py-2.5 rounded-xl text-sm">Descargar PDF</button>
                <button data-testid="share-print-pdf" onClick={() => openPDF(shareRx, shareUrl(shareRx))} className="flex-1 bg-brand-ink text-white font-semibold py-2.5 rounded-xl text-sm flex items-center justify-center gap-1.5"><Printer size={15} /> Abrir / Imprimir</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={tplDialog} onOpenChange={setTplDialog}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="consent-templates-dialog">
          <DialogHeader><DialogTitle className="font-outfit text-brand-ink">Plantillas de consentimiento</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Guarda y reutiliza plantillas de consentimiento sin gastar IA.</DialogDescription>
          <div className="flex gap-2">
            <Input data-testid="tpl-name-input" value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Nombre para guardar la plantilla actual" className="rounded-xl bg-stone-50 border-transparent focus:bg-white" />
            <button data-testid="tpl-save-btn" onClick={saveTemplate} className="shrink-0 bg-brand-sage text-white px-3 rounded-xl text-sm font-bold flex items-center gap-1"><BookmarkPlus size={15} /> Guardar</button>
          </div>
          <div className="mt-2 max-h-72 overflow-y-auto space-y-2" data-testid="tpl-list">
            {consentTemplates.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">Aún no tienes plantillas. Genera un consentimiento y guárdalo para reutilizarlo sin gastar IA.</p>
            ) : consentTemplates.map((t) => (
              <div key={t.template_id} data-testid={`tpl-${t.template_id}`} className="flex items-center gap-2 bg-stone-50 rounded-2xl p-3">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm text-brand-ink truncate">{t.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{t.procedure || "—"}</p>
                </div>
                <button data-testid={`tpl-apply-${t.template_id}`} onClick={() => applyTemplate(t)} className="shrink-0 text-xs font-bold text-brand-sageDark border border-brand-sage/40 rounded-lg px-3 py-1.5 hover:bg-brand-sage/10">Usar</button>
                <button data-testid={`tpl-del-${t.template_id}`} onClick={() => deleteTemplate(t)} className="shrink-0 text-muted-foreground hover:text-destructive p-1.5"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!detailItem} onOpenChange={() => setDetailItem(null)}>
        <DialogContent className="rounded-3xl max-w-sm" data-testid="dose-detail-dialog">
          <DialogHeader><DialogTitle className="font-outfit text-brand-ink">Detalle de dosis</DialogTitle></DialogHeader>
          {detailItem && (
            <div className="space-y-2 text-sm">
              <p className="font-semibold text-brand-ink">{detailItem.name} {detailItem.presentation}</p>
              {detailItem.dose_detail && <p className="text-muted-foreground">{detailItem.dose_detail}</p>}
              <div className="bg-brand-sage/10 rounded-xl p-3">
                <p className="text-[11px] font-bold uppercase tracking-wide text-brand-sageDark mb-1">Se imprime en la receta</p>
                <p className="text-brand-ink">{detailItem.name} {detailItem.presentation}</p>
                <p className="text-brand-ink">{(/(oral|v\.?o\.?|boca|tomar)/i.test(detailItem.route || "") || !detailItem.route) ? "Tomar" : "Aplicar"} {[detailItem.dose, detailItem.frequency, detailItem.duration && (/^por/i.test(detailItem.duration) ? detailItem.duration : `por ${detailItem.duration}`)].filter(Boolean).join(" ")}</p>
              </div>
              {detailItem.warning && <p className="text-xs text-destructive bg-destructive/5 rounded-lg px-2 py-1.5">⚠ {detailItem.warning}</p>}
              <AiDisclaimer />
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={pickerFor !== null} onOpenChange={() => setPickerFor(null)}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="saved-drugs-dialog">
          <DialogHeader><DialogTitle className="font-outfit text-brand-ink flex items-center gap-2"><BookMarked size={18} /> Mi banco de fármacos</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {savedDrugs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">Aún no has guardado fármacos. Completa un fármaco con IA y presiona <b>Guardar en banco</b> para reusarlo sin gastar IA.</p>
            ) : savedDrugs.map((d) => (
              <div key={d.saved_drug_id} className="bg-stone-50 rounded-xl p-3 flex items-start gap-2" data-testid={`saved-drug-${d.saved_drug_id}`}>
                <button onClick={() => applySaved(pickerFor, d)} className="flex-1 text-left min-w-0">
                  <p className="font-semibold text-sm text-brand-ink truncate">{d.name} {d.presentation}</p>
                  <p className="text-xs text-muted-foreground truncate">{[d.dose_mg_kg && `${d.dose_mg_kg}`, d.frequency, d.duration].filter(Boolean).join(" · ") || d.dose || "Sin posología guardada"}</p>
                  {d.dose_mg_kg && <p className="text-[10px] text-brand-sageDark font-semibold">Se recalcula por peso al cargar</p>}
                </button>
                <button onClick={() => deleteSaved(d.saved_drug_id)} className="text-muted-foreground hover:text-destructive p-1" title="Eliminar del banco" data-testid={`saved-drug-del-${d.saved_drug_id}`}><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
